import PendingCatalogRequests, { CatalogPublicationFilter, usePendingCatalogRequests } from "../catalogApprovals/PendingCatalogRequests";
import useCatalogAccess from "../useCatalogAccess";
import CatalogCreationPendingNotice, { isPendingCatalogCreation } from "../CatalogCreationPendingNotice";
import { useCatalogTaxonomyText } from "../catalogTaxonomyMessages";
import CorporateRecordBadge from "../CorporateRecordBadge";
import InventoryPolicyChip from "../InventoryPolicyChip";
import { managedByAdmin } from "../../utils/tenderManagement";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import axios from "../../../api/axiosInstance";
import useIsMobile from "../../../hooks/useIsMobile";
import AddCategoryPopup from "../popups/AddCategoryPopup";
import EditTaxonomyPopup from "../popups/EditTaxonomyPopup";
import { TaxonomyMergeActions } from "../popups/TaxonomyMergePopup";
import TaxonomyItemsPopup, {
  makeTaxonomyItemsScope,
  TaxonomyItemsLink,
} from "../popups/TaxonomyItemsPopup";
import {

  buildSearchBlob,
  formatItemsFamiliesDate,
  inventoryPolicyHint,
  inventoryPolicyLabel,
  readItemsFamiliesUser,
  resolveTaxonomyInventoryPolicy,
  truncateItemsFamiliesText,
} from "../itemsFamiliesUi";

function buildCategorySearchBlob(category) {
  return buildSearchBlob([
    category?.code,
    category?.name,
    category?.subfamily?.name,
    category?.subfamily?.family?.name,
    category?.description,
  ]);
}

export default function CategoriesTab({ businessUnitId }) {
  const t = useCatalogTaxonomyText();
  const currentUser = readItemsFamiliesUser();
  const activeBusinessUnitId = businessUnitId || currentUser?.active_business_unit_id;
  const access = useCatalogAccess(activeBusinessUnitId);
  const viewPermission = "purchasing.categories.view";
  const editPermissions = ["purchasing.categories.create", "purchasing.categories.update"];
  const exportPermissions = [viewPermission, "purchasing.categories.export"];
  const canViewCategories = access.can(viewPermission);
  const canCreateCategory = access.can("purchasing.categories.create");
  const canEditCategory = access.can(editPermissions);
  const canExport = access.can(exportPermissions);
  const isMobile = useIsMobile();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [notice, setNotice] = useState(null);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const [publicationFilter, setPublicationFilter] = useState("published");
  const pendingCatalog = usePendingCatalogRequests({ entityType: "category", businessUnitId: activeBusinessUnitId,
    enabled: canViewCategories && publicationFilter === "pending", refreshKey: isAddPopupOpen });
  const [editing, setEditing] = useState(null);
  const [itemsScope, setItemsScope] = useState(null);

  const fetchCategories = useCallback(
    async ({ quiet = false } = {}) => {
      if (!canViewCategories) {
        setCategories([]);
        setLoading(false);
        return;
      }

      if (!quiet) {
        setLoading(true);
      }

      const stamp = access.capture();
      try {
        const options = access.requestConfig(viewPermission);
        const response = await axios.get("/api/purchasing/categories", { ...options, params: { ...options.params, catalog: 1 } });
        if (!access.isCurrent(stamp, viewPermission)) return;
        const sorted = [...(response.data || [])].sort((left, right) => {
          const leftSubfamily = String(left?.subfamily?.name || "");
          const rightSubfamily = String(right?.subfamily?.name || "");
          if (leftSubfamily !== rightSubfamily) {
            return leftSubfamily.localeCompare(rightSubfamily, undefined, { sensitivity: "base" });
          }
          return String(left?.name || "").localeCompare(String(right?.name || ""), undefined, {
            sensitivity: "base",
          });
        });
        setCategories(sorted);
      } catch (requestError) {
        if (!access.isCurrent(stamp, viewPermission)) return;
        console.error("[CATEGORIES][LOAD][ERROR]", requestError);
        setNotice({
          tone: "error",
          message: "We could not load the category list for this business unit.",
        });
      } finally {
        if (!quiet && access.isCurrent(stamp, viewPermission)) {
          setLoading(false);
        }
      }
    },
    [canViewCategories, access, viewPermission]
  );

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const filteredCategories = useMemo(() => {
    const query = search.trim().toLowerCase();
    return categories.filter((category) => !query || buildCategorySearchBlob(category).includes(query));
  }, [categories, search]);

  const stats = useMemo(() => {
    const visibleFamilies = new Set(
      filteredCategories.map((category) => category?.subfamily?.family?.name).filter(Boolean)
    ).size;
    const visibleSubfamilies = new Set(
      filteredCategories.map((category) => category?.subfamily?.name).filter(Boolean)
    ).size;
    const described = filteredCategories.filter((category) => String(category?.description || "").trim()).length;

    return {
      visible: filteredCategories.length,
      families: visibleFamilies,
      subfamilies: visibleSubfamilies,
      described,
    };
  }, [filteredCategories]);

  const exportToExcel = async () => {
    if (!access.can(exportPermissions) || publicationFilter === "pending") return;
    const stamp = access.capture();
    try {
      const options = access.requestConfig(exportPermissions);
      const response = await axios.get("/api/purchasing/categories", { ...options, params: { ...options.params, catalog: 1, export: 1 } });
      if (!access.isCurrent(stamp, exportPermissions)) return;
      const query = search.trim().toLowerCase();
      const exportRecords = (response.data || []).filter((row) => !query || buildCategorySearchBlob(row).includes(query));
    if (!exportRecords.length) {
      setNotice({
        tone: "info",
        message: "There are no category rows in the current slice to export.",
      });
      return;
    }

    const exportRows = exportRecords.map((category) => ({
      Code: category?.code || "",
      Name: category?.name || "",
      Subfamily: category?.subfamily?.name || "",
      Family: category?.subfamily?.family?.name || "",
      InventoryPolicy: inventoryPolicyLabel(resolveTaxonomyInventoryPolicy(category, "category").policy),
      InventoryPolicySource: inventoryPolicyHint(resolveTaxonomyInventoryPolicy(category, "category"), "category"),
      Description: category?.description || "",
      CreatedAt: category?.created_at || "",
      UpdatedAt: category?.updated_at || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Categories");
    XLSX.writeFile(workbook, "categories.xlsx");

    setNotice({
      tone: "success",
      message: `${exportRecords.length} category rows exported to Excel.`,
    });
    } catch (_error) {
      if (access.isCurrent(stamp, exportPermissions)) setNotice({ tone: "error", message: "Could not export this catalog. Please try again." });
    }
  };

  const updateInventoryPolicy = async (category, inventoryPolicy) => {
    if (!access.can(editPermissions) || managedByAdmin(category)) return;
    const stamp = access.capture();
    try {
      const response = await axios.put(`/api/purchasing/categories/${category.id}`, {
        inventory_policy: inventoryPolicy || null,
      }, access.requestConfig(editPermissions));
      if (!access.isCurrent(stamp, editPermissions)) return;
      if (isPendingCatalogCreation(response.data)) {
        setPendingRequest(response.data.request);
        return;
      }
      setCategories((current) =>
        current.map((entry) => (Number(entry.id) === Number(category.id) ? { ...entry, ...response.data } : entry))
      );
      setNotice({ tone: "success", message: `${category?.name || "Category"} inventory policy updated.` });
    } catch (requestError) {
      if (!access.isCurrent(stamp, editPermissions)) return;
      console.error("[CATEGORIES][POLICY][ERROR]", requestError);
      setNotice({ tone: "error", message: "Could not update this inventory policy." });
      return false;
    }
  };

  const handleAdded = async (newCategory) => {
    await fetchCategories({ quiet: true });
    setNotice({
      tone: "success",
      message: `${newCategory?.name || "Category"} created successfully.`,
    });
  };

  const handleEdited = (updated) => {
    setCategories((current) => current.map((entry) => Number(entry.id) === Number(updated.id) ? { ...entry, ...updated } : entry));
    setNotice({ tone: "success", message: `${updated.name} updated successfully.` });
  };

  const renderEditButton = (category) => canEditCategory && !managedByAdmin(category) ? (
    <button type="button" className="spx-btn spx-btn--ghost" aria-label={t(`Edit Category: ${category.name}`)} onClick={() => setEditing(category)}>{t("Edit")}</button>
  ) : null;

  if (!canViewCategories) {
    return (
      <section className="spx-card pu-taxonomy">

        <div className="spx-empty">{t("You do not have permission to access categories.")}</div>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="spx-card pu-taxonomy">

        <div className="spx-empty">{t("Loading categories...")}</div>
      </section>
    );
  }

  return (
    <>
      <section className="spx-card pu-taxonomy">


        <div className="spx-head">
          <div className="spx-headMain">
            <div className="spx-kicker">{t("Family → Subfamily → Category → Subcategory → Item")}</div>
            <h2 className="spx-title">{t("Categories")}</h2>
            <p className="spx-subtitle">{t("Each category belongs to a subfamily and groups its items and subcategories.")}</p>
          </div>

          <div className="spx-actions">
            <TaxonomyMergeActions kind="category" records={categories} businessUnitId={activeBusinessUnitId} onChanged={() => fetchCategories({ quiet: true })} />
            {canCreateCategory ? (
              <button type="button" className="spx-btn spx-btn--primary" onClick={() => setIsAddPopupOpen(true)}>{t("+ Add Category")} </button>
            ) : null}
            {canExport && <button type="button" className="spx-btn spx-btn--warning" disabled={publicationFilter === "pending"} onClick={exportToExcel}>{t("Export Excel")} </button>}
          </div>
        </div>


        <div className="spx-toolbar">
          <div className="ifm-toolbarGrid ifm-toolbarGrid--two">
            <input
              type="search"
              className="spx-search"
              aria-label={t("Search")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("Search by code, category, subfamily, family or description...")}
            />
            {publicationFilter !== "pending" && <div className="spx-note">{t("Showing")} <strong>{filteredCategories.length}</strong> {t("of")} <strong>{categories.length}</strong> {t("categories.")} </div>}
          </div>
        </div>

        <CatalogPublicationFilter value={publicationFilter} onChange={setPublicationFilter} />

        {notice ? <div className={`spx-banner spx-banner--${notice.tone}`}>{t(notice.message)}</div> : null}

        {publicationFilter === "pending" ? <PendingCatalogRequests entityType="category" businessUnitId={activeBusinessUnitId} state={pendingCatalog} search={search} /> : <>
        <div className="spx-stats pu-taxonomy-stats">
          <div className="spx-stat">
            <div className="spx-statLabel">{t("Visible Categories")}</div>
            <div className="spx-statValue">{stats.visible}</div>
          </div>
          <div className="spx-stat">
            <div className="spx-statLabel">{t("Families Represented")}</div>
            <div className="spx-statValue">{stats.families}</div>
          </div>
          <div className="spx-stat">
            <div className="spx-statLabel">{t("Subfamilies Represented")}</div>
            <div className="spx-statValue">{stats.subfamilies}</div>
          </div>
          <div className="spx-stat">
            <div className="spx-statLabel">{t("With Description")}</div>
            <div className="spx-statValue">{stats.described}</div>
          </div>
        </div>

        {!filteredCategories.length ? (
          <div className="spx-empty">{t("No categories match the current search.")}</div>
        ) : isMobile ? (
          <div className="spx-mobileList">
            {filteredCategories.map((category) => (
              <article key={category.id} className="spx-mobileCard">
                {(() => {
                  const policy = resolveTaxonomyInventoryPolicy(category, "category");
                  return (
                    <>
                <div className="spx-mobileTop">
                  <div>
                    <div className="spx-code">{category?.code || `CATEGORY-${category.id}`}</div>
                    <TaxonomyItemsLink
                      scope={makeTaxonomyItemsScope("category", category?.id, category?.name)}
                      onOpen={setItemsScope}
                      strong
                      className="spx-strong"
                    >
                      {truncateItemsFamiliesText(category?.name || "-", 48)}<CorporateRecordBadge record={category} />
                    </TaxonomyItemsLink>
                  </div>
                  <InventoryPolicyChip policy={policy.policy} name={category.name} editable={canEditCategory && !managedByAdmin(category)} onChange={(value) => updateInventoryPolicy(category, value)} />
                </div>
                    </>
                  );
                })()}

                <div className="ifm-tagRow">
                  <TaxonomyItemsLink
                    scope={makeTaxonomyItemsScope("subfamily", category?.subfamily?.id, category?.subfamily?.name)}
                    onOpen={setItemsScope}
                    className="ifm-tag"
                  >
                    {category?.subfamily?.name || t("No subfamily")}
                  </TaxonomyItemsLink>
                  <TaxonomyItemsLink
                    scope={makeTaxonomyItemsScope(
                      "family",
                      category?.subfamily?.family?.id,
                      category?.subfamily?.family?.name
                    )}
                    onOpen={setItemsScope}
                    className="ifm-tag"
                  >
                    {category?.subfamily?.family?.name || t("No family")}
                  </TaxonomyItemsLink>
                </div>

                <div className="spx-mobileRows">
                  <div>
                    <div className="spx-mobileLabel">{t("Created")}</div>
                    <div className="spx-mobileValue">{formatItemsFamiliesDate(category?.created_at)}</div>
                  </div>
                  <div>
                    <div className="spx-mobileLabel">{t("Updated")}</div>
                    <div className="spx-mobileValue">{formatItemsFamiliesDate(category?.updated_at)}</div>
                  </div>
                </div>

                <div className="spx-mobileValue" style={{ marginTop: 10 }}>
                  {category?.description || "-"}
                </div>
                {canEditCategory && !managedByAdmin(category) && <div className="spx-actions" style={{ marginTop: 10 }}>{renderEditButton(category)}</div>}
              </article>
            ))}
          </div>
        ) : (
          <div className="spx-tableWrap">
            <table className="spx-table ifm-table--medium">
              <thead>
                <tr>
                  <th>{t("Code")}</th>
                  <th>{t("Category")}</th>
                  <th>{t("Subfamily")}</th>
                  <th>{t("Family")}</th>
                  <th>{t("Inventory Policy")}</th>
                  <th>{t("Description")}</th>
                  <th>{t("Created")}</th>
                  <th>{t("Updated")}</th>
                  {canEditCategory && <th>{t("Actions")}</th>}
                </tr>
              </thead>
              <tbody>
                {filteredCategories.map((category) => (
                  (() => {
                    const policy = resolveTaxonomyInventoryPolicy(category, "category");
                    return (
                      <tr key={category.id}>
                        <td title={category?.code || `CATEGORY-${category.id}`}>
                          <span className="spx-code">{category?.code || `CATEGORY-${category.id}`}</span>
                        </td>
                        <td title={category?.name || "-"}>
                          <TaxonomyItemsLink
                            scope={makeTaxonomyItemsScope("category", category?.id, category?.name)}
                            onOpen={setItemsScope}
                            strong
                            className="spx-strong"
                          >
                            {truncateItemsFamiliesText(category?.name || "-", 48)}<CorporateRecordBadge record={category} />
                          </TaxonomyItemsLink>
                        </td>
                        <td className="spx-muted">
                          <TaxonomyItemsLink
                            scope={makeTaxonomyItemsScope("subfamily", category?.subfamily?.id, category?.subfamily?.name)}
                            onOpen={setItemsScope}
                          >
                            {category?.subfamily?.name || "-"}
                          </TaxonomyItemsLink>
                        </td>
                        <td className="spx-muted">
                          <TaxonomyItemsLink
                            scope={makeTaxonomyItemsScope(
                              "family",
                              category?.subfamily?.family?.id,
                              category?.subfamily?.family?.name
                            )}
                            onOpen={setItemsScope}
                          >
                            {category?.subfamily?.family?.name || "-"}
                          </TaxonomyItemsLink>
                        </td>
                        <td>
                          <InventoryPolicyChip policy={policy.policy} name={category.name} editable={canEditCategory && !managedByAdmin(category)} onChange={(value) => updateInventoryPolicy(category, value)} />
                        </td>
                        <td className="spx-muted" title={category?.description || "-"}>
                          {truncateItemsFamiliesText(category?.description || "-", 72)}
                        </td>
                        <td className="spx-muted">{formatItemsFamiliesDate(category?.created_at)}</td>
                        <td className="spx-muted">{formatItemsFamiliesDate(category?.updated_at)}</td>
                        {canEditCategory && <td>{renderEditButton(category)}</td>}
                      </tr>
                    );
                  })()
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>}
      </section>

      {pendingRequest && <CatalogCreationPendingNotice request={pendingRequest} onClose={() => setPendingRequest(null)} />}

      <AddCategoryPopup
        existingRecords={categories}
        isOpen={isAddPopupOpen}
        onClose={() => setIsAddPopupOpen(false)}
        onAdded={handleAdded}
      />

      {editing && <EditTaxonomyPopup key={editing.id} kind="category" record={editing} existingRecords={categories} onClose={() => setEditing(null)} onSaved={handleEdited} />}

      <TaxonomyItemsPopup scope={itemsScope} onClose={() => setItemsScope(null)} />
    </>
  );
}
